import { Component, OnDestroy, OnInit } from '@angular/core';
import { MessageService, PrimeNGConfig } from 'primeng/api';
import { SpinnerService } from './core/services/spinner.service';
import { HttpClient } from '@angular/common/http';
import { NotificationService } from './core/services/notification.service';
import { GlobalObservableService } from './core/services/global-observable.service';
import { NavigationEnd, Router } from '@angular/router';
import { UserService } from './core/services/user.service';
import { Subscription } from 'rxjs';
import dayjs from 'dayjs';
import { PaymentService } from './core/services/payment.service';
import { LoggerService } from './core/services/logger.service';
import { environment } from '../environments/environment';

declare let gtag: Function;
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  spinner: boolean = false;
  private subscription!: Subscription;

  constructor(
    private primengConfig: PrimeNGConfig,
    http: HttpClient,
    private spinnerService: SpinnerService,
    private notificationService: NotificationService,
    private readonly ms: MessageService,
    private globalObservableService: GlobalObservableService,
    private router: Router,
    private userService: UserService,
    private paymentService: PaymentService,
    private logger: LoggerService
  ) {
    http.get<any>(`assets/es.json`).subscribe((rs) => {
      this.primengConfig.setTranslation(rs?.es);
    });
    this.spinnerService.httpProgress().subscribe((status: boolean) => {
      this.spinner = status;
    });
    this.eventSubscription();
  }
  ngOnInit() {
    // Fade out workspace transition overlay if present
    this.dismissWorkspaceTransition();

    // Configuración de Google Analytics para seguimiento de páginas
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const sanitizedUrl = this.sanitizeUrl(event.urlAfterRedirects);
        if (environment.gaId && typeof gtag === 'function') {
          gtag('config', environment.gaId, { page_path: sanitizedUrl });
        }
      }
    });
    this.primengConfig.ripple = true;
    this.notificationService.alert$.subscribe((rs) => {
      if (rs === 'COMPLETE') {
        this.ms.add({
          key: 'tst',
          severity: 'success',
          summary: 'Acción exitosa',
          detail: '¡Fuente de datos conectada con exito!',
        });
      } else {
        this.ms.add({
          key: 'tst',
          severity: 'error',
          summary: 'Acción fallida',
          detail: 'La fuente de datos no pudo añadirse',
        });
      }
    });
  }
  sanitizeUrl(url: string): string {
    // Expresión regular más específica para reemplazar UUIDs
    return url.replace(/\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi, '/ID');
  }
  eventSubscription() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.subscription = this.globalObservableService.getObservable().subscribe((data) => {
      if (data.type !== 'some_event') return;

      const user = this.getUserFromLocalStorage();
      if (!user) return;

      this.checkAndUpdateUserSubscription(user);
    });
  }

  private getUserFromLocalStorage(): any {
    const userString = localStorage.getItem('user');
    return userString ? JSON.parse(userString) : null;
  }

  /**
   * esta función debe detectar cuando un usuario cambia de plan para poder actualizar su información
   * @param parsedUser
   * @returns void
   */
  private checkAndUpdateUserSubscription(parsedUser: any) {
    //? PASO 1: si no llega la suscripción activa quiere decir que es pla free
    if (!parsedUser.latest_active_subscription) return;

    //? PASO 2: si llega la ultima suscripción activa entonces hay que tomar la fecha fin de esa suscripción
    const current_date = dayjs();
    const { is_active, end_date } = parsedUser.latest_active_subscription;
    const subscription_end_date = end_date ? dayjs(end_date) : null;

    //? PASO 3: si fecha actual no es mayor a la fecha fin entonces todavia está vigente
    if (!subscription_end_date || !current_date.isAfter(subscription_end_date) || !is_active) {
      return;
    }
    //? PASO 4: si no, entonces se debe actualizar la información del usuario
    this.userService.getUser().subscribe((user) => {
      this.userService.setUser(user);
      //PASO 4.1: Luego de actualizar se llama al servicio
      this.paymentService.getLastSubscription().subscribe({
        next: (subscription) => {
          // Si la suscripción no está activa, aparece el boton de pagar ahora
          if (!subscription.is_active) {
            localStorage.setItem('showExpiredPlan', 'true');
            this.globalObservableService.publish('subscription_inactive', { foo: 'bar' });
          }
        },
        error: (err) => {
          if (err.status === 404 && err.error.code === 53) {
            this.logger.error(err.error.message);
          } else {
            this.logger.error('Error', err);
          }
        },
      });
    });
  }

  private dismissWorkspaceTransition(): void {
    if (!localStorage.getItem('ws_transition')) return;
    localStorage.removeItem('ws_transition');

    // Brief delay so the user sees the transition, then fade out
    setTimeout(() => {
      const el = document.getElementById('workspace-transition');
      if (el) {
        el.style.opacity = '0';
        setTimeout(() => {
          el.style.display = 'none';
        }, 400);
      }
    }, 600);
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
